import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaRocket,
  FaMagic,
  FaExclamationTriangle,
  FaCheckCircle,
  FaLink,
  FaTimes,
  FaImage,
  FaFile,
  FaHandPointer,
  FaMousePointer,
} from "react-icons/fa";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import api from "@/api/config";
import CampaignWizard from "@/components/CampaignWizard/CampaignWizard";
import * as Dialog from "@radix-ui/react-dialog";
import { IoClose } from "react-icons/io5";
import { BsStars } from "react-icons/bs";
import "./CreateCampaignModal.scss";
import { LinkedinIcon } from "@/components/icons/LinkedinIcon";
import { WebIcon } from "@/components/icons/WebIcon";
import { SlCloudUpload } from "react-icons/sl";
import { SmallTwitterIcon } from "@/components/icons/SmallTwitterIcon";

interface CampaignFile {
  name: string;
  url: string;
  type: string;
  size?: number;
}

interface CampaignLink {
  label: string;
  url: string;
  type?: string;
}

interface CampaignFormData {
  name: string;
  product_description: string;
  company_name: string;
  sender_name: string;
  sender_title: string;
  sender_email: string;
  sender_phone?: string;
  social_linkedin?: string;
  social_twitter?: string;
  social_website?: string;
  campaign_files?: CampaignFile[];
  campaign_links?: CampaignLink[];
}

interface DescriptionScore {
  score: number;
  level: "poor" | "basic" | "good" | "excellent";
  message: string;
  suggestions: string[];
}

interface CreateCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
}

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../../shedcnUI/popover";

export const CreateCampaignModal: React.FC<CreateCampaignModalProps> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDescriptionEnhancing, setIsDescriptionEnhancing] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardDescription, setWizardDescription] = useState("");
  const [descriptionScore, setDescriptionScore] =
    useState<DescriptionScore | null>(null);
  const [subscription, setSubscription] = useState<any>(null);
  const [limitReached, setLimitReached] = useState(false);
  const [campaignFiles, setCampaignFiles] = useState<CampaignFile[]>([]);
  const [campaignLinks, setCampaignLinks] = useState<CampaignLink[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [newLinkLabel, setNewLinkLabel] = useState("");
  const [newLinkUrl, setNewLinkUrl] = useState("");

  useEffect(() => {
    const checkLimits = async () => {
      try {
        const response = await api.get("/api/subscription/current");
        setSubscription(response.data);
        const { usage, limits } = response.data;
        if (
          limits.campaigns_per_month !== -1 &&
          usage.campaigns_this_month >= limits.campaigns_per_month
        ) {
          setLimitReached(true);
        } else {
          setLimitReached(false);
        }
      } catch (error) {
        console.error("Error fetching subscription:", error);
      }
    };
    if (isOpen) {
      checkLimits();
      setCurrentStep(1); // Reset step when opened
    }
  }, [isOpen]);

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
  } = useForm<CampaignFormData>({
    mode: "onTouched",
  });

  const productDescription = watch("product_description");

  // Analyze description quality
  useEffect(() => {
    if (!productDescription || productDescription.length < 10) {
      setDescriptionScore(null);
      return;
    }

    const analyzeDescription = () => {
      const length = productDescription.length;
      const hasNumbers = /\d/.test(productDescription);
      const hasSpecifics =
        /\b(software|service|product|solution|platform|app|tool)\b/i.test(
          productDescription,
        );
      const hasTarget =
        /\b(business|company|startup|enterprise|customer|user|client)\b/i.test(
          productDescription,
        );
      const hasValue =
        /\b(help|improve|increase|reduce|save|boost|optimize|automate)\b/i.test(
          productDescription,
        );

      let score = 0;
      const suggestions = [];

      // Length scoring
      if (length < 50) {
        score += 10;
        suggestions.push("Add more details about your product/service");
      } else if (length < 100) {
        score += 25;
      } else if (length < 300) {
        score += 40;
      } else {
        score += 50;
      }

      // Content quality scoring
      if (hasSpecifics) score += 15;
      else
        suggestions.push("Specify what type of product or service you offer");

      if (hasTarget) score += 15;
      else suggestions.push("Define your target audience");

      if (hasValue) score += 15;
      else suggestions.push("Explain the value proposition or benefits");

      if (hasNumbers) score += 5;

      // Determine level and message
      let level: DescriptionScore["level"];
      let message: string;

      if (score < 30) {
        level = "poor";
        message =
          "Very basic description - AI enhancement strongly recommended";
      } else if (score < 50) {
        level = "basic";
        message = "Basic description - AI can help make it comprehensive";
      } else if (score < 75) {
        level = "good";
        message = "Good description - AI can help refine and optimize";
      } else {
        level = "excellent";
        message = "Excellent description - AI can help perfect the details";
      }

      setDescriptionScore({ score, level, message, suggestions });
    };

    const timer = setTimeout(analyzeDescription, 500); // Debounce
    return () => clearTimeout(timer);
  }, [productDescription]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post("/campaign/upload-file", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        setCampaignFiles([...campaignFiles, response.data.file]);
        toast.success("File uploaded successfully!");
      } else {
        toast.error("Failed to upload file");
      }
    } catch (error) {
      toast.error("Failed to upload file");
      console.error(error);
    } finally {
      setUploadingFile(false);
      // Reset input
      e.target.value = "";
    }
  };

  const removeFile = (index: number) => {
    setCampaignFiles(campaignFiles.filter((_, i) => i !== index));
  };

  const addLink = () => {
    if (!newLinkLabel.trim() || !newLinkUrl.trim()) {
      toast.error("Please provide both label and URL");
      return;
    }

    // Validate URL
    try {
      new URL(newLinkUrl);
    } catch {
      toast.error("Please enter a valid URL");
      return;
    }

    setCampaignLinks([
      ...campaignLinks,
      { label: newLinkLabel, url: newLinkUrl },
    ]);
    setNewLinkLabel("");
    setNewLinkUrl("");
    toast.success("Link added!");
  };

  const removeLink = (index: number) => {
    setCampaignLinks(campaignLinks.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: CampaignFormData) => {
    // Don't submit if wizard is active
    if (showWizard) {
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        ...data,
        campaign_files: campaignFiles,
        campaign_links: campaignLinks,
      };
      const response = await api.post("/campaign/new", submitData);
      toast.success("Campaign created successfully!");
      onClose(); // Close the modal
      navigate(`/campaign/${response.data.campaign_uuid}/use`);
    } catch (error) {
      toast.error("Failed to create campaign");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEnhanceWithAI = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent any form submission
    e.stopPropagation(); // Stop event bubbling

    // Clear any existing errors when opening wizard
    clearErrors();

    const description = productDescription;
    if (!description || description.length < 10) {
      toast.error(
        "Please provide at least a basic description (10+ characters) to enhance",
      );
      return;
    }

    setWizardDescription(description);
    setShowWizard(true);
    setIsDescriptionEnhancing(true);
  };

  const handleWizardComplete = (enhancedDescription: string) => {
    setValue("product_description", enhancedDescription);
    setShowWizard(false);
    setIsDescriptionEnhancing(false);
    toast.success("Description enhanced successfully!");
  };

  const handleWizardCancel = () => {
    setShowWizard(false);
    setIsDescriptionEnhancing(false);
  };

  const getScoreColor = (level: string) => {
    switch (level) {
      case "poor":
        return "#ef4444";
      case "basic":
        return "#f59e0b";
      case "good":
        return "#10b981";
      case "excellent":
        return "#8b5cf6";
      default:
        return "#6b7280";
    }
  };

  const handleNextStep = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (limitReached) {
      toast.error(
        "You have reached your campaign limit. Please upgrade your plan to create more campaigns.",
      );
      return;
    }
    let fieldsToValidate: (keyof CampaignFormData)[] = [];
    if (currentStep === 1) {
      fieldsToValidate = ["name", "product_description"];
    } else if (currentStep === 2) {
      fieldsToValidate = [
        "company_name",
        "sender_name",
        "sender_title",
        "sender_email",
        "sender_phone",
      ];
    } else if (currentStep === 3) {
      fieldsToValidate = [
        "social_linkedin",
        "social_twitter",
        "social_website",
      ];
    }

    const isStepValid = await trigger(fieldsToValidate);
    if (isStepValid) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
    } else {
      toast.error("Please check the fields for errors before proceeding.");
    }
  };

  const handlePrevStep = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const onInvalid = (errors: any) => {
    toast.error("Please fix the errors in the form before creating the campaign.");
    
    // If there are errors in hidden steps, help the user find them
    if (errors.name || errors.product_description) {
      setCurrentStep(1);
    } else if (errors.company_name || errors.sender_name || errors.sender_title || errors.sender_email) {
      setCurrentStep(2);
    } else if (errors.social_linkedin || errors.social_twitter || errors.social_website) {
      setCurrentStep(3);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[555] bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-[555] w-full max-w-[800px] translate-x-[-50%] translate-y-[-50%] bg-white rounded-[12px] p-8 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] max-h-[90vh] overflow-y-auto overflow-x-hidden new-campaign-page">
          <Dialog.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <IoClose className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </Dialog.Close>

          <div
            className="campaign-container"
            style={{
              margin: 0,
              padding: 0,
              minHeight: "unset",
              background: "transparent",
              maxWidth: "100%",
            }}
          >
            <div className="text-center mb-6 mt-2">
              <Dialog.Title className="text-[22px] font-bold text-[#17150B] flex items-center justify-center gap-3 m-0">
                Create New Campaign
                <Popover>
                  <PopoverTrigger>
                    <span className="bg-[#FEC2FE] text-[#17150B] text-xs font-semibold px-2.5 py-1 rounded-md flex items-center gap-1 cursor-pointer">
                      <BsStars /> AI Powered
                      <FaMousePointer />
                    </span>
                  </PopoverTrigger>
                  <PopoverContent
                    align="center"
                    sideOffset={10}
                    className="w-[320px] p-5 "
                  >
                    <div className="flex flex-col gap-3 py-1">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-[#FEC2FE] flex items-center justify-center text-[#17150B]">
                          <FaMagic />
                        </div>
                        <h3 className="text-sm font-bold text-[#17150B]">
                          AI-Powered Campaign Enhancement
                        </h3>
                      </div>
                      <p className="text-xs text-[#7D7B78] leading-relaxed">
                        Our AI can help you create a comprehensive campaign
                        description by asking targeted questions about your
                        goals, audience, and value proposition.
                      </p>
                      <ul className="space-y-2">
                        {[
                          {
                            emoji: "✨",
                            text: "Get personalized follow-up questions",
                          },
                          {
                            emoji: "🎯",
                            text: "Improve targeting with audience insights",
                          },
                          {
                            emoji: "📈",
                            text: "Increase engagement with optimized messaging",
                          },
                          {
                            emoji: "🤖",
                            text: "Powered by advanced AI language models",
                          },
                        ].map((item, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-2 text-[11px] text-[#17150B]"
                          >
                            <span>{item.emoji}</span>
                            <span>{item.text}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </PopoverContent>
                </Popover>
              </Dialog.Title>
              <Dialog.Description className="text-[#7D7B78] text-[14px] mt-2">
                Set up your email campaign with AI-powered personalization
              </Dialog.Description>
            </div>
            {limitReached && (
              <div className="alert alert-warning flex items-center  mb-4 mx-8 rounded-[14px] bg-light-yellow bg-orange-200 px-5 py-3 cursor-pointer hover:bg-orange-100 transition-colors">
                <div className="space-x-2">
                  <h4 className="flex items-center space-x-2 gap-2 font-bold mb-1">
                    <FaExclamationTriangle /> Campaign Limit Reached
                  </h4>
                  <p className="pl-4">
                    You have reached your monthly campaign limit of{" "}
                    {subscription?.limits?.campaigns_per_month}. Please upgrade
                    your plan to create more campaigns..
                  </p>
                </div>
                <button
                  className="bg-brand-blue text-white px-4 py-1.5 rounded-md text-sm font-semibold hover:bg-blue-600 transition-colors flex-none h-fit whitespace-nowrap"
                  onClick={() => navigate("/subscription")}
                >
                  Upgrade Plan
                </button>
              </div>
            )}

            <div className="flex justify-center mb-8">
              <div className="flex items-center gap-[6px]">
                {[1, 2, 3, 4].map((step) => (
                  <div
                    key={step}
                    className={`h-[3px] w-12 rounded-full transition-colors duration-300 ${step <= currentStep ? "bg-[#006EFF]" : "bg-gray-200"}`}
                  />
                ))}
              </div>
            </div>
            <form
              onSubmit={handleSubmit(onSubmit, onInvalid)}
              className="campaign-form max-w-[600px] mx-auto border-t border-[#E3E2E7] pt-10"
            >
              {/* Step 1: Campaign Details */}
              <div style={{ display: currentStep === 1 ? "block" : "none" }}>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-5"
                >
                  <div className="form-group">
                    <label
                      htmlFor="name"
                      className="text-[13px] font-bold text-[#17150B] mb-2"
                    >
                      Campaign Name
                      <span className="asterisk text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      className={`w-full px-4 py-2.5 rounded-lg border border-[#7D7B78] focus:outline-none focus:ring-2 focus:ring-[#006EFF]/20 focus:border-[#006EFF] transition-all text-sm ${errors.name ? "border-red-500" : "border-[#7D7B78]"}`}
                      {...register("name", {
                        required: "Campaign name is required",
                      })}
                      placeholder="e.g., Spring Sale 2024"
                    />
                    {errors.name && (
                      <span className="text-red-500 text-xs mt-1 block">
                        {errors.name.message}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label
                      htmlFor="product_description"
                      className="text-[13px] font-bold text-[#17150B] mb-2"
                    >
                      Product/Service Description
                      <span className="asterisk text-red-500">*</span>
                    </label>

                    <textarea
                      id="product_description"
                      className={`w-full px-4 py-3 rounded-lg border border-[#7D7B78] focus:outline-none focus:ring-2 focus:ring-[#006EFF]/20 focus:border-[#006EFF] transition-all resize-none text-[14px] ${errors.product_description ? "border-red-500" : "border-[#7D7B78]"}`}
                      rows={6}
                      {...register("product_description", {
                        required: "Product description is required",
                        minLength: {
                          value: 10,
                          message: "Please provide at least 10 characters",
                        },
                      })}
                      placeholder="Describe your product or service, target audience, and value proposition..."
                    />
                    {errors.product_description && (
                      <span className="text-red-500 text-xs mt-1 block">
                        {errors.product_description.message}
                      </span>
                    )}

                    {/* Description Score */}
                    {descriptionScore && (
                      <motion.div
                        className="description-analysis mt-3"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <div className="score-header flex items-center justify-between mb-1">
                          <div className="score-bar w-full bg-gray-200 h-2 rounded-full overflow-hidden mr-3">
                            <div
                              className="score-fill h-full"
                              style={{
                                width: `${descriptionScore.score}%`,
                                backgroundColor: getScoreColor(
                                  descriptionScore.level,
                                ),
                              }}
                            />
                          </div>
                          <span
                            className="score-label text-xs font-semibold whitespace-nowrap"
                            style={{
                              color: getScoreColor(descriptionScore.level),
                            }}
                          >
                            {descriptionScore.score}% Complete
                          </span>
                        </div>

                        <div className="score-message flex items-center gap-2 text-sm mt-2">
                          {descriptionScore.level === "poor" && (
                            <FaExclamationTriangle className="text-red-500" />
                          )}
                          {descriptionScore.level === "excellent" && (
                            <FaCheckCircle className="text-purple-500" />
                          )}
                          <span>{descriptionScore.message}</span>
                        </div>

                        {descriptionScore.suggestions.length > 0 &&
                          descriptionScore.level !== "excellent" && (
                            <div className="score-suggestions mt-2 p-3 bg-gray-50 rounded-lg text-sm">
                              <strong className="text-gray-700">
                                Suggestions to improve:
                              </strong>
                              <ul className="list-disc pl-5 mt-1 text-gray-600">
                                {descriptionScore.suggestions.map(
                                  (suggestion, index) => (
                                    <li key={index}>{suggestion}</li>
                                  ),
                                )}
                              </ul>
                            </div>
                          )}
                      </motion.div>
                    )}

                    {/* Enhance with AI Button - Below the textarea */}
                    {productDescription && productDescription.length >= 10 && (
                      <motion.div
                        className="enhance-section mt-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        <button
                          type="button"
                          className="btn-enhance-ai w-full flex justify-center items-center gap-2 py-2.5 bg-[#006eff] text-white rounded-lg font-medium shadow-sm hover:shadow transition-all"
                          onClick={handleEnhanceWithAI}
                          disabled={limitReached || isSubmitting || isDescriptionEnhancing}
                        >
                          <FaMagic /> Enhance with AI Wizard
                        </button>

                        {/* Campaign Wizard */}
                        <AnimatePresence>
                          {showWizard && wizardDescription.length > 0 && (
                            <motion.div
                              className="wizard-container mt-4"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }
                              }}
                            >
                              <CampaignWizard
                                initialDescription={wizardDescription}
                                onComplete={handleWizardComplete}
                                onCancel={handleWizardCancel}
                                inline={true}
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              </div>

              {/* Step 2: Sender Information */}
              <div style={{ display: currentStep === 2 ? "block" : "none" }}>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-0"
                >
                  <div className="form-group">
                    <label
                      htmlFor="company_name"
                      className="text-[13px] font-bold text-[#17150B] mb-2"
                    >
                      <div className="flex items-center gap-2">
                        <span>
                          Company Name
                          <span className="asterisk text-red-500">*</span>
                        </span>
                      </div>
                    </label>
                    <input
                      type="text"
                      id="company_name"
                      className={`w-full px-4 py-2.5 rounded-xl border border-[#7D7B78] focus:outline-none focus:ring-2 focus:ring-[#006EFF]/20 focus:border-[#006EFF] transition-all text-[14px] ${errors.company_name ? "border-red-500" : "border-[#7D7B78]"}`}
                      {...register("company_name", {
                        required: "Company name is required",
                      })}
                      placeholder="Your Company Inc."
                    />
                    {errors.company_name && (
                      <span className="text-red-500 text-xs mt-1 block">
                        {errors.company_name.message}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                      <label
                        htmlFor="sender_name"
                        className="text-[13px] font-bold text-[#17150B] mb-2"
                      >
                        Your Name
                        <span className="asterisk text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="sender_name"
                        className={`w-full px-4 py-2.5 rounded-xl border border-[#7D7B78] focus:outline-none focus:ring-2 focus:ring-[#006EFF]/20 focus:border-[#006EFF] transition-all text-[14px] ${errors.sender_name ? "border-red-500" : "border-[#7D7B78]"}`}
                        {...register("sender_name", {
                          required: "Sender name is required",
                        })}
                        placeholder="John Doe"
                      />
                      {errors.sender_name && (
                        <span className="text-red-500 text-xs mt-1 block">
                          {errors.sender_name.message}
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <label
                        htmlFor="sender_title"
                        className="text-[13px] font-bold text-[#17150B] mb-2"
                      >
                        Your Title
                        <span className="asterisk text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="sender_title"
                        className={`w-full px-4 py-2.5 rounded-xl border border-[#7D7B78] focus:outline-none focus:ring-2 focus:ring-[#006EFF]/20 focus:border-[#006EFF] transition-all text-[14px] ${errors.sender_title ? "border-red-500" : "border-[#7D7B78]"}`}
                        {...register("sender_title", {
                          required: "Sender title is required",
                        })}
                        placeholder="Sales Manager"
                      />
                      {errors.sender_title && (
                        <span className="text-red-500 text-xs mt-1 block">
                          {errors.sender_title.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                      <label
                        htmlFor="sender_email"
                        className="text-[13px] font-bold text-[#17150B] mb-2"
                      >
                        <div className="flex items-center gap-2">
                          <span>
                            Email Address
                            <span className="asterisk text-red-500">*</span>
                          </span>
                        </div>
                      </label>
                      <input
                        type="email"
                        id="sender_email"
                        className={`w-full px-4 py-2.5 rounded-xl border border-[#7D7B78] focus:outline-none focus:ring-2 focus:ring-[#006EFF]/20 focus:border-[#006EFF] transition-all text-[14px] ${errors.sender_email ? "border-red-500" : "border-[#7D7B78]"}`}
                        {...register("sender_email", {
                          required: "Email is required",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address",
                          },
                        })}
                        placeholder="john@company.com"
                      />
                      {errors.sender_email && (
                        <span className="text-red-500 text-xs mt-1 block">
                          {errors.sender_email.message}
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <label
                        htmlFor="sender_phone"
                        className="text-[13px] font-bold text-[#17150B] mb-2"
                      >
                        <div className="flex items-center gap-2">
                          <span>Phone Number</span>
                        </div>
                      </label>
                      <input
                        type="tel"
                        id="sender_phone"
                        className="w-full px-4 py-2.5 rounded-xl border border-[#7D7B78] focus:outline-none focus:ring-2 focus:ring-[#006EFF]/20 focus:border-[#006EFF] transition-all text-[14px]"
                        {...register("sender_phone")}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Step 3: Social Media & Website */}
              <div style={{ display: currentStep === 3 ? "block" : "none" }}>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className=""
                  style={{ marginTop: 0, padding: "24px" }}
                >
                  <h2 className="section-title text-base font-medium text-[#17150B] mb-1.5">
                    Social Media & Website
                  </h2>
                  <p className="text-sm text-[#7D7B78] mb-5">
                    Click a mailbox to manage profile/password or sync
                    credentials.
                  </p>

                  <div className="form-group">
                    <div className="flex flex-col mb-2.5">
                      <div className="flex gap-2.5 items-center">
                        <div className="flex items-center justify-center bg-[#F8F8F8] rounded-xl size-[50px] min-w-[50px]">
                          <LinkedinIcon />
                        </div>
                        <input
                          type="url"
                          id="social_linkedin"
                          className={`w-full px-4 py-2.5 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#006EFF]/20 focus:border-[#006EFF] transition-all text-[14px] ${errors.social_linkedin ? "border-red-500" : "border-[#7D7B78]"}`}
                          {...register("social_linkedin", {
                            pattern: {
                              value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/i,
                              message: "Please enter a valid URL (e.g., https://...)"
                            }
                          })}
                          placeholder="https://www.linkedin.com/in/yourprofile"
                        />
                      </div>
                      {errors.social_linkedin && (
                        <span className="text-red-500 text-xs mt-1 ml-[60px]">
                          {errors.social_linkedin.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="flex flex-col mb-2.5">
                      <div className="flex gap-2.5 items-center">
                        <div className="flex items-center justify-center bg-[#F8F8F8] rounded-xl size-[50px] min-w-[50px]">
                          <SmallTwitterIcon />
                        </div>
                        <input
                          type="url"
                          id="social_twitter"
                          className={`w-full px-4 py-2.5 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#006EFF]/20 focus:border-[#006EFF] transition-all text-[14px] ${errors.social_twitter ? "border-red-500" : "border-[#7D7B78]"}`}
                          {...register("social_twitter", {
                            pattern: {
                              value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/i,
                              message: "Please enter a valid URL (e.g., https://...)"
                            }
                          })}
                          placeholder="https://twitter.com/yourhandle"
                        />
                      </div>
                      {errors.social_twitter && (
                        <span className="text-red-500 text-xs mt-1 ml-[60px]">
                          {errors.social_twitter.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="flex flex-col mb-2.5">
                      <div className="flex gap-2.5 items-center">
                        <div className="flex items-center justify-center bg-[#F8F8F8] rounded-xl size-[50px] min-w-[50px]">
                          <WebIcon />
                        </div>
                        <input
                          type="url"
                          id="social_website"
                          className={`w-full px-4 py-2.5 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#006EFF]/20 focus:border-[#006EFF] transition-all text-[14px] ${errors.social_website ? "border-red-500" : "border-[#7D7B78]"}`}
                          {...register("social_website", {
                            pattern: {
                              value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/i,
                              message: "Please enter a valid URL (e.g., https://...)"
                            }
                          })}
                          placeholder="https://www.yourcompany.com"
                        />
                      </div>
                      {errors.social_website && (
                        <span className="text-red-500 text-xs mt-1 ml-[60px]">
                          {errors.social_website.message}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Step 4: Files & Links */}
              <div style={{ display: currentStep === 4 ? "block" : "none" }}>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className=""
                >
                  <h2 className="section-title text-base font-medium text-[#17150B] mb-1.5">
                    Upload Files (Logos, Docs, Applications)
                  </h2>
                  <p className="text-sm text-[#7D7B78] mb-5">
                    Upload logos, documents, applications, or add links that
                    will be available in your campaign emails.
                  </p>

                  {/* File Upload Section */}
                  <div className="form-group mb-6">
                    <div className="file-upload-area relative border border-dashed border-[#E3E2E7] rounded-xl p-6 text-center hover:border-[#006EFF] transition-colors">
                      <input
                        type="file"
                        id="file-upload"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        onChange={handleFileUpload}
                        disabled={uploadingFile}
                        accept="image/*,.pdf,.doc,.docx,.txt,.exe,.dmg,.msi,.zip,.rar"
                      />
                      <div className="pointer-events-none flex flex-col items-center gap-4 text-gray-500">
                        {uploadingFile ? (
                          <>
                            <div className="w-6 h-6 border-2 border-[#006EFF] border-t-transparent rounded-full animate-spin"></div>
                            <span>Uploading...</span>
                          </>
                        ) : (
                          <>
                            <SlCloudUpload className="text-2xl text-gray-400" />
                            <span className="text-[#7D7B78] font-normal text-sm">
                              Choose File or Drag & Drop
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {campaignFiles.length > 0 && (
                      <div className="uploaded-files mt-4 space-y-2">
                        {campaignFiles.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-gray-50 border border-gray-100 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div className="text-[#006EFF]">
                                {file.type === "image" ? (
                                  <FaImage />
                                ) : (
                                  <FaFile />
                                )}
                              </div>
                              <div className="flex flex-col">
                                <span className="text-sm font-medium text-gray-700">
                                  {file.name}
                                </span>
                                <span className="text-xs text-gray-400">
                                  {file.type}
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="text-gray-400 hover:text-red-500 p-1 rounded-full hover:bg-red-50 transition-colors"
                              onClick={() => removeFile(index)}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Links Section */}
                  <div className="form-group">
                    <label className="text-[13px] font-bold text-[#17150B] mb-2">
                      <div className="flex items-center gap-2">
                        <span>Add Links</span>
                      </div>
                    </label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="text"
                        className="flex-1 px-3 py-2 rounded-lg border border-[#7D7B78] focus:outline-none focus:ring-2 focus:ring-[#006EFF]/20 focus:border-[#006EFF] text-[14px]"
                        placeholder="Link Label (e.g., Product Demo)"
                        value={newLinkLabel}
                        onChange={(e) => setNewLinkLabel(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && addLink()}
                      />
                      <input
                        type="url"
                        className="flex-1 px-3 py-2 rounded-lg border border-[#7D7B78] focus:outline-none focus:ring-2 focus:ring-[#006EFF]/20 focus:border-[#006EFF] text-[14px]"
                        placeholder="https://example.com"
                        value={newLinkUrl}
                        onChange={(e) => setNewLinkUrl(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && addLink()}
                      />
                      <button
                        type="button"
                        className="px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors whitespace-nowrap"
                        onClick={addLink}
                      >
                        <FaLink /> Add
                      </button>
                    </div>

                    {campaignLinks.length > 0 && (
                      <div className="added-links mt-4 space-y-2">
                        {campaignLinks.map((link, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-gray-50 border border-gray-100 rounded-lg"
                          >
                            <div className="flex items-center gap-3 overflow-hidden">
                              <FaLink className="text-[#006EFF] shrink-0" />
                              <div className="flex flex-col overflow-hidden">
                                <span className="text-sm font-medium text-gray-700 truncate">
                                  {link.label}
                                </span>
                                <a
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-[#006EFF] hover:underline truncate"
                                >
                                  {link.url}
                                </a>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="text-gray-400 hover:text-red-500 p-1 rounded-full hover:bg-red-50 transition-colors shrink-0 ml-2"
                              onClick={() => removeLink(index)}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>

              {/* Form Navigation / Actions */}
              <div className="flex justify-center gap-2 flex-col items-center mt-8 pt-4 border-t border-gray-100">
                {currentStep < totalSteps ? (
                  <button
                    key="next-button"
                    type="button"
                    className="bg-[#006EFF] min-w-[234px] text-white px-8 py-2.5 rounded-lg text-[14px] font-semibold hover:bg-blue-700 transition-colors shadow-sm"
                    onClick={(e) => handleNextStep(e)}
                    disabled={isSubmitting || isDescriptionEnhancing || limitReached}
                  >
                    Next Step
                  </button>
                ) : (
                  <button
                    key="submit-button"
                    type="submit"
                    className="bg-[#006EFF] text-white px-8 py-2.5 rounded-lg text-[14px] font-semibold hover:bg-blue-500 transition-colors shadow-sm flex items-center gap-2"
                    disabled={isSubmitting || limitReached}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Creating...
                      </>
                    ) : (
                      "Create Campaign"
                    )}
                  </button>
                )}
                {currentStep > 1 ? (
                  <button
                    key="back-button"
                    type="button"
                    className="px-6 py-2.5 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-colors text-[14px]"
                    onClick={(e) => handlePrevStep(e)}
                    disabled={isSubmitting}
                  >
                    Back
                  </button>
                ) : (
                  <div></div>
                )}
              </div>
            </form>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
